import {ChangeDetectionStrategy, Component, Input, OnInit, OnDestroy} from "@angular/core";
import {filter, map, takeUntil, withLatestFrom} from "rxjs/operators";
import _, {get} from "lodash";
import {ConfirmModalLoaderPayload} from "@ispm/layout/classes/confirm-modal-loader-payload";
import {Observable} from "rxjs/Observable";
import {WorkflowActiveTabConfig} from "@ispm/workflow/interfaces/workflow-ui-config.interface";
import {WorkflowConfigService} from "@ispm/workflow/services/workflow-config.service";
import {Subject} from "rxjs/Subject";
import {TranslateService} from "@ngx-translate/core";
import {ModalLoaderNgjsService} from "@ispm/layout/services/modal-loader-ngjs.service";
import {QuestionnairesService} from "@ispm/widgets/components/questionnaires/services/questionnaire.service";
import {TestBarActionTypes, TestBarEnableConfirmButtonAction} from "@ispm/test/store/test-bar.actions";
import {Store} from "@ngrx/store";
import {IgenomicsState} from "@ispm/core/interfaces/genomics-state.interface";
import {StringUtils} from "@ispm/shared/utils/String.utils";
import {TestConfiguration} from "@ispm/test/interfaces/test-configuration.interface";
import {TestService} from "@ispm/test/services/test.service";
import { documentsPreviewJobName, pathwaysSelectionJobName } from '@ispm-client/modules/pathways';
import {AuthenticationService} from "@ispm/user/services/authentication.service";
import {AberrationsService} from "@ispm/aberrations/services/aberrations.service";
import {FilterService} from "../../../../filter/services/filter.service";
import {distinctUntilChanged} from "rxjs/operators";
import { ActivatedRoute, Router } from "@angular/router";
import {RouterManagerService} from "@ispm/core/services/router-manager.service";


@Component ({
  selector: 'gen-test-tab',
  templateUrl: './test-tab.component.html',
  styleUrls: ['./test-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class TestTabComponent implements OnInit, OnDestroy {

  private tabsConfig$: Observable<WorkflowActiveTabConfig[]>;
  private destroyed$: Subject<any> = new Subject();
  private tabChanged$: Subject<string> = new Subject();
  @Input() private activeWorkflowConfirmationButton: string = '';
  @Input() private isChecklistTabPresent: boolean = false;
  private testConfiguration: TestConfiguration;

  public selectedTab: string;
  public isFixedHeightTabPane: boolean;
  public verticalLeftContainerPatientView: boolean;
  public tabsTitles$: Observable<{ name: string; }[]>;
  public configuration;
  public workflowBarItems;
  @Input() originalStateName: string;
  public readonly needToConvertCamelCase = ['layout-engine'];
  @Input() pageReload: boolean = false;

  constructor(private store: Store<IgenomicsState>,
              private activatedRoute: ActivatedRoute,
              private route: Router,
              private authService: AuthenticationService,
              private workflowConfigService: WorkflowConfigService,
              private aberrationsService: AberrationsService,
              private filterService: FilterService,
              private _translate: TranslateService,
              private _modalLoaderService: ModalLoaderNgjsService,
              private questionnairesService: QuestionnairesService,
              private testService : TestService,
              private routerManagerService: RouterManagerService) {
  }

  ngOnInit() {
      this.isChecklistValid();
      this.testConfiguration = this.testService.testConfiguration;
      if(this.testConfiguration){
        this.isFixedHeightTabPane = !this.testConfiguration.isPathwayTest;
        this.verticalLeftContainerPatientView = !!this.testConfiguration.VerticalLeftContainerPatientView;
      }
      this.initTabsConfig();
      this.tabsConfig$.pipe(takeUntil(this.destroyed$.asObservable())).subscribe(tabConfig => {
        let activeTab = tabConfig.find(tab => tab.isActive === true);
        // Changing to QueryParameters has a issue with LCO Application.
        if (get(this.routerManagerService.parameters, 'payload')) {
          activeTab = tabConfig.find(tab => get(tab, 'payload') === get(this.routerManagerService.parameters, 'payload'));
        }
        // Page Reload will be true, if dashboard is retriggering the url with state being set.
        if ((this.pageReload || this.routerManagerService.backToDashboard || this.routerManagerService.browserRefresh) && get(this.routerManagerService.queryParameters, 'payload')){
          activeTab = tabConfig.find(tab => get(tab, 'payload') === get(this.routerManagerService.queryParameters, 'payload'));
        }
        if (activeTab) {
          this.onTabChanged(activeTab.title);
        } else {
          const currentStateName = this.routerManagerService.currentState;
          const selectedSubState = currentStateName.substr(this.originalStateName.length + 1);
          const selectedConfig = selectedSubState ? tabConfig.find(tab => tab.type === selectedSubState) : null;

          if (selectedConfig) {
            this.selectedTab = selectedConfig.title;
          } else {
            this.onTabChanged(tabConfig[0].title);
          }
        }
      });
      this.validateClinicalDate();

  }

  private initTabsConfig() {
    this.tabsConfig$ = this.workflowConfigService.getUiConfig().pipe(
      takeUntil(this.destroyed$.asObservable()),
      takeUntil(this.workflowConfigService.getActiveWorkflow(false).pipe(filter(wf => !wf))),// listen until workflow cleared
      map(config => config.activeTabs)
    );
    this.tabsTitles$ = this.tabsConfig$.map(configs => configs.map(config => ({name: config.title})));
    this.tabChanged$.pipe(withLatestFrom(this.tabsConfig$),takeUntil(this.destroyed$.asObservable()))
      .subscribe(([tabName, tabConfig]) => {
        tabConfig.forEach(tab => {
          if (tab.isActive && tab.title !== tabName) delete tab.isActive;
        });
        const currentTab = tabConfig.find(tab => tab.title === tabName);
        const tabType = (tabConfig.find(tab => tab.title === tabName).type);
        if (this.selectedTab !== tabName) {
          const replaceUrl:boolean = this.selectedTab === undefined ? false : true;
          this.selectedTab = tabName;
          currentTab.isActive = true;
          const current = this.routerManagerService.currentState;
          const destination = (this.needToConvertCamelCase.indexOf(tabType) > -1) ? tabType.replace(/[-_](.)/g, (_, char) => char.toUpperCase()) : tabType;
          const payload = get(currentTab, 'payload');

          let queryParams = this.routerManagerService.queryParameters;
          let fragments = this.routerManagerService.fragments;
          if ((payload) && (this.routerManagerService.parameters.payload !== payload)) {
            const routePayload = {
              payload: payload
            }
            queryParams = {...queryParams, ...routePayload};
            this.route.navigate([destination], {
              relativeTo: this.activatedRoute,
              queryParams,
              replaceUrl : replaceUrl
            });
          } else if (current !== destination) {
            // fragment information is required for pathways previewMode.
            let skipHistory: boolean = (/true/i).test(this.routerManagerService.skipHistory);
            this.route.navigate([destination], {
              relativeTo: this.activatedRoute,
              queryParams: queryParams,
              fragment: fragments,
              replaceUrl: skipHistory
            });
          }
        }
      });
  }

  /**
   * Checks the validity of the checklist response
   */
  private isChecklistValid() {
    // GEN-153183 check the validity of generate button when the checklist tab is present
    if (this.activeWorkflowConfirmationButton === documentsPreviewJobName && this.isChecklistTabPresent) {
      this.questionnairesService.getQuestionnaireData().pipe(
        takeUntil(this.destroyed$.asObservable())).subscribe((data) => {
        // GEN-153116 Sign-off button is disabled for Oncology pathways.
        // Try to find questionnaire for the current application and current organization
        // A questionnaire can't be found in 2 cases:
        // 1. it's not relevant to the current application and the current organization
        // 2. it's not found for the current organization even if current application should have a questionnaire, this case
        // is fixed by adding a check for checklist tab visibility
        (data && data.questionnaire && data.questionnaire.isChecklistValid) ? this.dispatchToStore(true, '')
          : this.dispatchToStore(false, 'Please update the Checklist!');
      });
    }
  }


  /**
   * Vaidate clinical date for PNC and based on the validation enable or disable the confimration button
   */
  validateClinicalDate() {
    this.store.select('tests', 'diagnosticOrders', 'order')
      .pipe(distinctUntilChanged(),takeUntil(this.destroyed$.asObservable()),
        map(order => {
          const clinicalDate = Object(order)["clinicalDate"]
          const testType = Object(order)["testType"]
          if (testType === "PNC" && clinicalDate === null) {
            this.dispatchToStore(false, '');
          }
        })
      ).subscribe();
  }

  /**
   * Dispatches data to store
   * @param enableButton holds boolean value to enable button
   * @param tooltipText holds tooltipText
   */
  private dispatchToStore(enableButton: boolean, tooltipText: string) {
    this.store.dispatch(new TestBarEnableConfirmButtonAction(enableButton));
    this.store.dispatch({
      type: TestBarActionTypes.EnableConfirmButtonTooltip,
      payload: tooltipText
    });
  }

  public cssClassFormat(className: string) {
    return StringUtils.cssClassFormat(className);
  }

  public onTabChanged($event: string) {
    this.testService.readTestConfiguration().take(1).subscribe(testConfig => {
      if(testConfig.decisionAutoSave) {
        this.testService.checkDecision();
      }
    })
    const eventName: string = this._translate.instant($event);
    let selectedTab: string = '';
    if(this.selectedTab) {
      selectedTab = this._translate.instant(this.selectedTab);
    }
    if(selectedTab === 'Checklist' && eventName !== selectedTab && this.questionnairesService.isChecklistFormUpdated$.value) {
      this._modalLoaderService.popupModal('confirm',
        ConfirmModalLoaderPayload.builder()
          .setTitle(this._translate.instant('DISCARD_DATA') || 'Discard Data')
          .setContent('Unsaved changes have been made to the Checklist. Would you like to continue (changes will not be saved)?')
          .setDeclineButtonText('Cancel')
          .setApproveButtonText('Continue'),
        'ALERT').subscribe(response => {
        if (get(response, 'payload') === 1) {
          this.isChecklistValid();
          this.tabChanged$.next($event);
          this.questionnairesService.isChecklistFormUpdated$.next(false);
        }
      });
    } else {
      this.isChecklistValid();
      this.tabChanged$.next($event);
    }
  }

  ngOnDestroy() {
    this.tabsConfig$ = null;
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
